import React, { createContext, useContext, useEffect, useState } from 'react';
import { tokenManager } from '../../utils/tokenManager';
import { securityAuditLogger } from '../../utils/securityAuditLogger';
import SessionTimeoutWarning from './SessionTimeoutWarning';

interface SecurityContextType {
  isSecurityEnabled: boolean;
  tokenInfo: any;
  auditLogs: any[];
  refreshSecurityData: () => void;
  forceLogout: () => void;
  extendSession: () => Promise<void>;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurityContext must be used within a SecurityProvider');
  }
  return context;
};

interface SecurityProviderProps {
  children: React.ReactNode;
  enableSessionTimeout?: boolean;
  sessionTimeoutWarningMinutes?: number;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({
  children,
  enableSessionTimeout = false, // Temporarily disabled - causing modal to block interactions
  sessionTimeoutWarningMinutes = 5
}) => {
  const [isSecurityEnabled] = useState(true);
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);

  useEffect(() => {
    // Initialize security monitoring
    initializeSecurity();
    
    // Set up periodic security checks
    const securityCheckInterval = setInterval(() => {
      refreshSecurityData();
    }, 60000); // Check every minute

    return () => {
      clearInterval(securityCheckInterval);
    };
  }, []);

  const initializeSecurity = () => {
    // Log security provider initialization
    securityAuditLogger.logSecurityEvent('security_provider_initialized', {
      timestamp: new Date().toISOString(),
      enableSessionTimeout,
      sessionTimeoutWarningMinutes
    });

    refreshSecurityData();
  };

  const refreshSecurityData = () => {
    try {
      // Update token info
      const currentTokenInfo = tokenManager.getTokenInfo();
      setTokenInfo(currentTokenInfo);

      // Update recent audit logs
      const recentLogs = securityAuditLogger.getAuditLogs({
        startDate: new Date(Date.now() - 24 * 60 * 60 * 1000) // Last 24 hours
      });
      setAuditLogs(recentLogs);

      // Check for suspicious activity patterns
      checkForSuspiciousActivity(recentLogs);
    } catch (error) {
      console.error('Failed to refresh security data:', error);
      securityAuditLogger.logSecurityEvent('security_data_refresh_error', {
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  };

  const checkForSuspiciousActivity = (logs: any[]) => {
    const now = Date.now();
    const last10Minutes = now - 10 * 60 * 1000;
    
    // Check for multiple failed login attempts
    const recentFailedLogins = logs.filter(log => 
      log.type === 'auth_login' && 
      log.data?.success === false &&
      new Date(log.timestamp).getTime() > last10Minutes
    );

    if (recentFailedLogins.length >= 3) {
      securityAuditLogger.logSuspiciousActivity('multiple_failed_logins', {
        count: recentFailedLogins.length,
        timeWindow: '10 minutes',
        timestamps: recentFailedLogins.map(log => log.timestamp)
      });
    }

    // Check for rapid token refresh attempts
    const recentTokenRefreshes = logs.filter(log => 
      log.type === 'token_refresh_success' &&
      new Date(log.timestamp).getTime() > last10Minutes
    );

    if (recentTokenRefreshes.length >= 5) {
      securityAuditLogger.logSuspiciousActivity('excessive_token_refreshes', {
        count: recentTokenRefreshes.length,
        timeWindow: '10 minutes',
        timestamps: recentTokenRefreshes.map(log => log.timestamp)
      });
    }
  };

  const forceLogout = () => {
    securityAuditLogger.logSecurityEvent('forced_logout', {
      timestamp: new Date().toISOString(),
      reason: 'user_initiated'
    });
    
    tokenManager.forceSessionTimeout();
  };

  const extendSession = async () => {
    try {
      await tokenManager.getValidToken();
      
      securityAuditLogger.logSecurityEvent('session_extended', {
        timestamp: new Date().toISOString(),
        method: 'user_action'
      });
      
      refreshSecurityData();
    } catch (error) {
      securityAuditLogger.logSecurityEvent('session_extension_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
      
      throw error;
    }
  };

  const contextValue: SecurityContextType = {
    isSecurityEnabled,
    tokenInfo,
    auditLogs,
    refreshSecurityData,
    forceLogout,
    extendSession
  };

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
      
      {/* Session Timeout Warning */}
      {enableSessionTimeout && (
        <SessionTimeoutWarning
          warningThreshold={sessionTimeoutWarningMinutes}
          onExtendSession={extendSession}
          onForceLogout={forceLogout}
        />
      )}
    </SecurityContext.Provider>
  );
};